import React from 'react';
import { useAppTheme } from '../../contexts/AppThemeContext';

interface LogoProps {
  size?: 'small' | 'medium' | 'large';
  showWordmark?: boolean;
  className?: string;
  onClick?: () => void;
  href?: string;
}

const BRANDING_ASSETS = {
  logo: {
    light: '/assets/branding/logo-light.svg',
    dark: '/assets/branding/logo-dark.svg',
  },
  icon: {
    light: '/assets/branding/icon-light.svg',
    dark: '/assets/branding/icon-dark.svg',
  },
} as const;

const sizeMap = {
  small: {
    icon: 'w-6 h-5',
    wordmark: 'h-5',
  },
  medium: {
    icon: 'w-10 h-8',
    wordmark: 'h-8',
  },
  large: {
    icon: 'w-16 h-12',
    wordmark: 'h-12',
  },
} as const;

export function AppLogo({
  size = 'medium',
  showWordmark = true,
  className = '',
  onClick,
  href,
}: LogoProps) {
  const { theme } = useAppTheme();
  const sizes = sizeMap[size];
  const variant = theme === 'dark' ? 'dark' : 'light';
  const src = showWordmark ? BRANDING_ASSETS.logo[variant] : BRANDING_ASSETS.icon[variant];
  const dimensions = showWordmark ? sizes.wordmark : sizes.icon;

  const logoContent = (
    <div className={`flex items-center leading-none ${className}`}>
      <img
        src={src}
        alt="Edge Technologies"
        className={`${dimensions} w-auto flex-shrink-0 object-contain transition-transform group-hover:scale-105`}
      />
    </div>
  );

  if (href) {
    return (
      <a href={href} className="group" onClick={onClick}>
        {logoContent}
      </a>
    );
  }

  if (onClick) {
    return (
      <button onClick={onClick} className="group">
        {logoContent}
      </button>
    );
  }

  return logoContent;
}

export function AppIcon({
  size = 'medium',
  className = '',
}: Omit<LogoProps, 'showWordmark'>) {
  return <AppLogo size={size} showWordmark={false} className={className} />;
}
